import type * as zod from 'zod';

import type { CartesianPose } from '@sb/geometry';
import { posesAreEqual } from '@sb/geometry';
import { namespace, warn } from '@sb/log';
import type {
  StepTooltip,
  TCPFrame,
  TCPOffsetOption,
} from '@sb/motion-planning';
import { ArmTarget } from '@sb/motion-planning';
import type { StepFailure } from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';

import Step from '../Step';
import {
  resolveMotionChildTooltip,
  savedPoseFrame,
} from '../tooltipResolution';

import Arguments from './Arguments';
import Variables from './Variables';

const ns = namespace('ArcStep');

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

export default class ArcStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {};
  }

  /**
   * Placeholder method for the play logic.
   * The play method is not used directly for the arc step.
   */
  public async _play(): Promise<void> {
    // NO _play method for ArcStep. Use getArmTarget with moveArmToV2 or Weld.
  }

  private static overwriteOrientation(
    pose: CartesianPose,
    orientationSource: CartesianPose,
  ): CartesianPose {
    return {
      ...pose,
      i: orientationSource.i,
      j: orientationSource.j,
      k: orientationSource.k,
      w: orientationSource.w,
    };
  }

  public async getArmTarget({
    parentCompletedCount,
    previousTargetPose,
    // The frame the carried `previousTargetPose` is genuinely expressed in:
    // option name + (when known) the equipment instance that defines it
    // (mirrors the Waypoint param). When present and it differs from this
    // arc's commanded frame, `maintainStartOrientation` re-expresses the
    // carried orientation into the commanded frame before overwriting with
    // it. Absent (regular moves, constant speed, weld flag-OFF), the
    // overwrites use the carry verbatim — exactly today's behavior. SUP-4006.
    previousTargetTCPFrame = undefined,
    // The tooltip the parent pins — a Weld step's torch, or the enclosing Move
    // step's tooltip while constant speed is on (mirrors the Waypoint param).
    // Both halves: a Weld torch names an option only, while a Move step's
    // tooltip can name its equipment outright.
    forcedTooltip = undefined,
    // The robot's Active Tooltip and the enclosing Move step's tooltip, what
    // this arc's symbolic `tcpOption` resolves through (mirrors the Waypoint
    // params).
    activeTooltip = undefined,
    parentTooltip = undefined,
  }: {
    parentCompletedCount: number;
    previousTargetPose?: CartesianPose;
    previousTargetTCPFrame?: TCPFrame;
    forcedTooltip?: TCPFrame;
    activeTooltip?: TCPFrame;
    parentTooltip?: StepTooltip;
  }): Promise<
    | {
        target: ArmTarget[];
        poseForPreviousTarget: CartesianPose;
        poseForPreviousTargetTCPFrame: TCPFrame | undefined;
      }
    | StepFailure
  > {
    const resultA = await this._getArmTarget({
      index: 0,
      forcedTooltip,
      parentCompletedCount,
      activeTooltip,
      parentTooltip,
    });

    const resultB = await this._getArmTarget({
      index: 1,
      forcedTooltip,
      parentCompletedCount,
      activeTooltip,
      parentTooltip,
    });

    if ('failure' in resultA) {
      return resultA;
    }

    if ('failure' in resultB) {
      return resultB;
    }

    // When maintainStartOrientation is enabled, overwrite the via and end
    // orientations with the start (previous target) orientation. This ensures
    // the poses sent to the planner match, and that poseForPreviousTarget
    // carries the correct orientation for the next segment.
    if (this.args.maintainStartOrientation && previousTargetPose) {
      const targetA = resultA.target;
      const targetB = resultB.target;

      // Fail loud when the carried frame names equipment that was deleted
      // outright (mirrors the Waypoint relative-to-current guard): its offset
      // is then unrecoverable, and re-expressing through a substitute would
      // command a wrong orientation. A DISABLED record is fine — the
      // re-expression below resolves by id, disabled included.
      if (
        previousTargetTCPFrame?.gripperID != null &&
        !this.routineContext.hasEquipmentDevice(
          previousTargetTCPFrame.gripperID,
        )
      ) {
        warn(
          ns`getArmTarget`,
          'maintainStartOrientation: the carried start frame names ' +
            'equipment that no longer exists; failing loud. Disable the ' +
            'matchRelativeToCurrentFrames feature flag to revert.',
          {
            gripperID: previousTargetTCPFrame.gripperID,
            tcpOption: previousTargetTCPFrame.tcpOption,
          },
        );

        return {
          failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
          failureReason:
            'This arc maintains its start orientation, but the previous ' +
            'waypoint was saved with a tooltip that no longer exists on ' +
            "this robot, so the arc's orientation can't be determined. To " +
            'fix this, re-save the previous waypoint, or restore the ' +
            'missing equipment.',
          error: new Error(
            'maintainStartOrientation: predecessor TCP frame equipment deleted',
          ),
        };
      }

      // The carry is genuinely expressed in `previousTargetTCPFrame`; this
      // arc commands `resultB.poseForPreviousTargetTCPFrame` (the (option,
      // gripperID) pair the carry-out tag names and command consumption
      // resolves — always defined once a segment parsed). When both frames
      // are known, re-express the carried pose into the commanded frame
      // BEFORE the orientation overwrites: copying it verbatim under a
      // differing frame commands the predecessor's teach-frame quaternion as
      // a torch orientation and carries the same lie out
      // (`reexpressTooltipPose` short-circuits equal frames). With no
      // incoming tag (regular moves, constant speed, weld flag-OFF) the
      // carry is used verbatim: today's behavior, byte-identical. SUP-4006.
      const orientationSource =
        previousTargetTCPFrame && resultB.poseForPreviousTargetTCPFrame
          ? this.routineContext.reexpressTooltipPose(
              previousTargetPose,
              previousTargetTCPFrame,
              resultB.poseForPreviousTargetTCPFrame,
            )
          : previousTargetPose;

      if ('pose' in targetA) {
        targetA.pose = ArcStep.overwriteOrientation(
          targetA.pose,
          orientationSource,
        );
      }

      if ('pose' in targetB) {
        targetB.pose = ArcStep.overwriteOrientation(
          targetB.pose,
          orientationSource,
        );
      }

      return {
        target: [targetA, targetB],
        poseForPreviousTarget: ArcStep.overwriteOrientation(
          resultB.poseForPreviousTarget,
          orientationSource,
        ),
        poseForPreviousTargetTCPFrame: resultB.poseForPreviousTargetTCPFrame,
      };
    }

    return {
      target: [resultA.target, resultB.target],
      poseForPreviousTarget: resultB.poseForPreviousTarget,
      poseForPreviousTargetTCPFrame: resultB.poseForPreviousTargetTCPFrame,
    };
  }

  public async _getArmTarget({
    index,
    forcedTooltip = undefined,
    parentCompletedCount,
    activeTooltip = undefined,
    parentTooltip = undefined,
  }: {
    index: number;
    forcedTooltip?: TCPFrame;
    parentCompletedCount: number;
    activeTooltip?: TCPFrame;
    parentTooltip?: StepTooltip;
  }): Promise<
    | {
        target: ArmTarget;
        poseForPreviousTarget: CartesianPose;
        poseForPreviousTargetTCPFrame: TCPFrame | undefined;
      }
    | StepFailure
  > {
    // TODO Implement getArmTarget for ArcStep. Current implementation is a placeholder that just moves us to the "end" of the arc.
    const argsTarget = this.args.targets[index];

    const parseTarget = (armPosition: any) => {
      const target = {
        motionKind: 'arc',
        ...armPosition,
        calibrationPose: armPosition.pose,
        blendRadius: 0,
        stopHere: this.args.stopHere,
        maintainStartOrientation: this.args.maintainStartOrientation,
      };

      // remove jointAngle fields
      target.jointAngles = undefined;
      target.calibrationPose = undefined;

      const pose = { ...armPosition.pose };

      // Shared with the Waypoint step, so a Move step with mixed
      // waypoint/arc children cannot resolve them differently. An arc has no
      // relative-to-current read/write frame; the parent pin covers both a
      // Weld step's torch and a constant-speed Move step's tooltip.
      const resolved = resolveMotionChildTooltip({
        tcpOption: this.args.tcpOption,
        // An arc that stored no instance of its own keeps the one the pose was
        // taught with.
        gripperID: this.args.gripperID ?? armPosition.gripperID,
        savedFrame: savedPoseFrame(armPosition),
        // A Weld step's torch, or the Move step's tooltip while constant
        // speed is on — the child's own selection is locked out and the UI
        // shows "Defined in Weld step" / "Defined in Move step".
        parentForcedTooltip: forcedTooltip,
        readWriteFrame: undefined,
        activeTooltip,
        parentTooltip,
      });

      target.effectiveTCPOption = resolved.effectiveTCPOption;
      target.gripperID = resolved.gripperID;

      const parsedTarget = ArmTarget.safeParse(target);

      if (!parsedTarget.success) {
        throw new Error('Move arm target is not valid');
      }

      return { target: parsedTarget.data, pose };
    };

    let target: ArmTarget;
    let anchor: any; // Space Item
    let poseForPreviousTarget: CartesianPose;

    try {
      if ('positionListID' in argsTarget) {
        const parseResult = parseTarget(
          this.routineFrame.getPositionListEntry(
            argsTarget.positionListID,
            argsTarget.positionListIndex ?? parentCompletedCount,
          ),
        );

        const targetSpace = this.routineFrame.getSpaceItem(
          argsTarget.positionListID,
        );

        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;

        if (targetSpace.anchoredToID) {
          anchor = this.routineFrame.getSpaceItem(targetSpace.anchoredToID);
        }
      } else if ('expression' in argsTarget) {
        const expressionTarget = await this.routineFrame.evaluateExpression(
          argsTarget.expression,
        );

        // if expression evaluates to an array, use the first item
        // this makes it easier to use 'SinglePosition' space objects.
        const parseResult = parseTarget(
          Array.isArray(expressionTarget)
            ? expressionTarget[0]
            : expressionTarget,
        );

        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
      } else {
        const parseResult = parseTarget(argsTarget);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
      }

      if (
        'positionListID' in argsTarget &&
        anchor &&
        anchor.kind === 'localAccuracyCalibration'
      ) {
        if ('pose' in target) {
          target.pose = this.routineContext.getCalibratedGoal(
            target.pose,
            anchor,
          );
        } else if ('jointAngles' in target && 'calibrationPose' in target) {
          const calibratedPose = this.routineContext.getCalibratedGoal(
            target.calibrationPose,
            anchor,
          );

          // do not change the original target object if pose did not change
          if (!posesAreEqual(target.calibrationPose, calibratedPose)) {
            const newJointAngles =
              await this.routineContext.getJointAnglesForCartesianSpacePose(
                calibratedPose,
                'joint',
                false,
              );

            if (newJointAngles) {
              target.jointAngles = newJointAngles;
            }
          }
        }
      }

      const commandedTCPOption = (
        target as { effectiveTCPOption?: TCPOffsetOption }
      ).effectiveTCPOption;

      return {
        target,
        poseForPreviousTarget,
        // The TCP frame this segment commanded (the parent reads it as the
        // carry tag): option + the gripperID downstream consumption pairs it
        // with, so a later relative read resolves the same arithmetic.
        poseForPreviousTargetTCPFrame: commandedTCPOption
          ? {
              tcpOption: commandedTCPOption,
              gripperID: (target as { gripperID?: string }).gripperID,
            }
          : undefined,
      };
    } catch (error) {
      return {
        failure: {
          kind: FailureKind.InvalidRoutineLoadedFailure,
        },
        failureReason: error.message,
        error,
      };
    }
  }

  public hasStopEarlyConditions(): boolean {
    return false;
  }

  public async evaluateStopEarlyCondition(): Promise<boolean> {
    return false;
  }
}
